import { ReactNode } from 'react';
import cn from 'classnames';

import SortSelect from './SortSelect';
import StoreAvailabilityToggle from './StoreAvailabilityToggle';

import styles from './PlpSortBar.module.scss';

export interface PlpSortBarProps {
  locale?: string;
  /**
   * Result-count text. Sits at the start of the bar on desktop, on its own row
   * above the filters on mobile.
   */
  stats?: ReactNode;
  /**
   * Mobile-only filters trigger, rendered above the sort row. Hidden from `md`
   * up, where filtering lives in the sidebar. Omit it when filtering has its own
   * row outside the bar (see `FEATURE_FLAG_PLP_FILTERS_V2`).
   */
  filtersButton?: ReactNode;
  className?: string;
}

/**
 * PLP sort + store-availability bar.
 *
 * From `md` up this is a single bordered bar: stats at the start, the store
 * toggle and sort control trailing. Below `md` the design breaks the bar apart —
 * stats stays at the top while the store toggle and sort drop below the filter
 * pills and applied-filter chips, directly above the grid. The bar collapses to
 * `display: contents` there so those pieces become rows of the header itself and
 * can be re-ordered around siblings this component does not render; see
 * `headerSortBarV2` in `Header.module.scss`.
 */
const PlpSortBar = ({
  locale,
  stats,
  filtersButton,
  className,
}: PlpSortBarProps) => {
  return (
    <div className={cn(styles.bar, className)}>
      {stats ? <div className={styles.stats}>{stats}</div> : null}
      {filtersButton ? (
        <div className={styles.filters}>{filtersButton}</div>
      ) : null}
      <div className={styles.controls}>
        <div className={styles.store}>
          <StoreAvailabilityToggle locale={locale} />
        </div>
        <div className={styles.sort}>
          <SortSelect locale={locale} />
        </div>
      </div>
    </div>
  );
};

export default PlpSortBar;
